import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Hammer, Car, Cpu, Shirt, Home, Wrench, Plus, Star, Users, Eye, Flame } from "lucide-react";
import { toast } from "sonner";

/**
 * Plyx-flavoured Makers Exhibition. Same backend collection
 * (maker_showcases), dark aesthetic, surfaces under /plyx/makers so
 * Plyx's viral video traffic discovers the makers and sends sponsor
 * interest signals.
 *
 * Submissions still go via the AfriMarket auth flow (/makers/new) so
 * maker identity stays unified in one place.
 */

const CATEGORY_META = {
  automobile:   { label: "Automobile",     Icon: Car,    color: "#FF2D7A" },
  crafts:       { label: "Crafts",         Icon: Hammer, color: "#FFB42D" },
  electronics:  { label: "Electronics",    Icon: Cpu,    color: "#00E0C7" },
  fashion:      { label: "Fashion",        Icon: Shirt,  color: "#A45DFF" },
  architecture: { label: "Architecture",   Icon: Home,   color: "#FF6B00" },
  other:        { label: "Other",          Icon: Wrench, color: "#737B7E" },
};

const PlyxMakers = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || null;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams({ sort: "featured", limit: "30" });
    if (activeCategory) q.set("category", activeCategory);
    api.get(`/makers/showcases?${q.toString()}`)
      .then((r) => setItems(r.data?.items || []))
      .catch(() => toast.error("Could not load the showcase"))
      .finally(() => setLoading(false));
  }, [activeCategory]);

  const setCategory = (cat) => {
    if (!cat) searchParams.delete("category");
    else searchParams.set("category", cat);
    setSearchParams(searchParams, { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="plyx-makers-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div
          className="absolute inset-0 opacity-50 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 80% 10%, #FFB42D 0%, transparent 35%), radial-gradient(circle at 10% 90%, #FF2D7A 0%, transparent 35%), radial-gradient(circle at 60% 90%, #00E0C7 0%, transparent 35%)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-5 py-12 sm:py-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[11px] uppercase tracking-[0.3em] font-semibold text-[#FFB42D] mb-4">
            <Hammer className="w-3 h-3" /> Plyx Talent Discovery
          </div>
          <h1 className="font-display text-4xl sm:text-6xl tracking-tight max-w-3xl">
            Build it. Show it.{" "}
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}
            >
              Get backed.
            </span>
          </h1>
          <p className="text-sm sm:text-base text-white/70 max-w-2xl mt-4 leading-relaxed">
            African makers, engineers, and craft artists — your work in one feed. Sponsors and
            investors scrolling Plyx land here daily looking for the next thing to back.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/makers/new?app=afrimarket"
              data-testid="plyx-makers-submit-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-[#0A0A0A] text-sm"
              style={{ background: "linear-gradient(135deg, #FFB42D 0%, #FF2D7A 100%)" }}
            >
              <Plus className="w-4 h-4" /> Submit your work
            </Link>
            <a
              href="#plyx-makers-grid"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/25 text-white text-sm hover:bg-white/10"
            >
              Browse makers
            </a>
          </div>
        </div>
      </section>

      {/* Category filter */}
      <div className="max-w-5xl mx-auto px-5 mt-7" id="plyx-makers-grid">
        <div className="flex gap-2 flex-wrap" data-testid="plyx-makers-category-filter">
          <button
            onClick={() => setCategory(null)}
            data-testid="plyx-makers-cat-all"
            className={`px-4 py-2 rounded-full text-xs font-semibold border transition-colors ${
              !activeCategory
                ? "bg-white text-[#0A0A0A] border-white"
                : "border-white/15 text-white/70 hover:text-white hover:border-white/30"
            }`}
          >
            All
          </button>
          {Object.entries(CATEGORY_META).map(([key, meta]) => {
            const { Icon, label, color } = meta;
            const active = activeCategory === key;
            return (
              <button
                key={key}
                onClick={() => setCategory(key)}
                data-testid={`plyx-makers-cat-${key}`}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold border transition-colors ${
                  active
                    ? "text-[#0A0A0A] border-transparent"
                    : "border-white/15 text-white/70 hover:text-white hover:border-white/30"
                }`}
                style={active ? { background: color } : {}}
              >
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-5xl mx-auto px-5 pb-24 mt-7">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-white/30" />
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-3xl border border-white/10 bg-white/5 p-12 text-center" data-testid="plyx-makers-empty">
            <Hammer className="w-12 h-12 mx-auto text-white/30 mb-3" />
            <div className="font-semibold">No makers in this category yet</div>
            <div className="text-xs text-white/50 mt-1">Be the first to drop your build on Plyx.</div>
            <Link
              to="/makers/new?app=afrimarket"
              className="mt-5 inline-block px-5 py-2 rounded-full bg-white text-[#0A0A0A] text-xs font-bold"
            >
              Submit your work
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((s) => {
              const meta = CATEGORY_META[s.category] || CATEGORY_META.other;
              const { Icon } = meta;
              const cover = s.image_urls?.[0];
              return (
                <Link
                  key={s.showcase_id}
                  to={`/plyx/makers/${s.showcase_id}`}
                  data-testid={`plyx-makers-card-${s.showcase_id}`}
                  className="group block rounded-2xl bg-white/5 border border-white/10 overflow-hidden transition-all hover:border-white/30 hover:-translate-y-0.5"
                >
                  <div className="relative aspect-[4/3] bg-black/40 overflow-hidden">
                    {cover ? (
                      <img
                        src={cover}
                        alt={s.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/20">
                        <Icon className="w-12 h-12" />
                      </div>
                    )}
                    {s.is_featured && (
                      <div className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFB42D] text-[#0A0A0A] text-[10px] font-bold uppercase tracking-wider">
                        <Star className="w-2.5 h-2.5" /> Featured
                      </div>
                    )}
                    <div
                      className="absolute bottom-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-white"
                      style={{ background: meta.color }}
                    >
                      <Icon className="w-2.5 h-2.5" /> {meta.label}
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="font-semibold line-clamp-2 leading-snug">{s.title}</div>
                    <div className="text-xs text-white/55 mt-1">
                      {s.creator_name} · {s.country}
                      {s.region ? ` · ${s.region}` : ""}
                    </div>
                    <div className="flex items-center gap-4 mt-3 text-xs text-white/55">
                      <span className="inline-flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" /> {s.sponsor_count}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Eye className="w-3.5 h-3.5" /> {s.view_count}
                      </span>
                      {s.funding_goal > 0 && (
                        <span className="inline-flex items-center gap-1 text-[#FFB42D]">
                          <Flame className="w-3.5 h-3.5" /> £{s.funding_goal.toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlyxMakers;
